/**
 * Rewrites legacy ReactQuill class-based formatting to inline styles.
 *
 * ReactQuill stores alignment and indentation as CSS classes (`ql-align-center`,
 * `ql-indent-2`) and ships the matching CSS only inside its own editor stylesheet — which
 * never travels with a sent email. So on content authored in the old text editor, those
 * classes are inert in a recipient's inbox: centering and indentation silently vanish.
 *
 * Applied when *loading* existing content (templates, drafts, scheduled emails) into the
 * new HTML editor, it re-expresses that formatting as inline styles that survive the send
 * and that TinyMCE understands. Re-saving then persists the fix. New TinyMCE content
 * already uses inline styles, so it never carries these classes.
 */

/** Only genuine legacy Quill content carries these class prefixes. */
const QUILL_MARKER = /ql-(align|indent)-/;

export function normalizeLegacyQuillHtml(html: string): string {
  // Fast path: anything without a Quill class — new TinyMCE output, hand-authored HTML,
  // empty strings — is returned byte-for-byte, so this never re-serializes or otherwise
  // perturbs content it has no reason to touch.
  if (!html || !QUILL_MARKER.test(html)) return html;
  // DOMParser is browser-only; on a server/SSR path leave the content unchanged rather
  // than throw. The editor this feeds only ever runs in the browser.
  if (typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.body.querySelectorAll('[class*="ql-align-"], [class*="ql-indent-"]').forEach((node) => {
    const element = node as HTMLElement;

    (['center', 'right', 'justify', 'left'] as const).forEach((align) => {
      if (element.classList.contains(`ql-align-${align}`)) {
        element.style.textAlign = align;
        element.classList.remove(`ql-align-${align}`);
      }
    });

    // Quill renders 3em of indent per level; margin-left reproduces that and is
    // well-supported across mail clients.
    for (let level = 1; level <= 9; level += 1) {
      if (element.classList.contains(`ql-indent-${level}`)) {
        element.style.marginLeft = `${level * 3}em`;
        element.classList.remove(`ql-indent-${level}`);
      }
    }

    // Drop a now-empty class attribute so the output stays clean.
    if (element.classList.length === 0) element.removeAttribute('class');
  });

  return doc.body.innerHTML;
}

export default normalizeLegacyQuillHtml;
